import { useCallback, useEffect, useRef, useState } from "react"

import { useNotebookSchedule } from "../../hooks/useNotebookSchedule"
import { useReportConversation } from "../../hooks/useReportConversation"
import {
  useReportEnvironment,
  type ReportEnvironment,
} from "../../hooks/useReportEnvironment"
import { useReportHighlights } from "../../hooks/useReportHighlights"
import { useReportLiveRevisions } from "../../hooks/useReportLiveRevisions"
import { useReportSteering } from "../../hooks/useReportSteering"
import {
  reportErrorText,
  saveNotebookRevision,
  saveNotebookTitle,
  unpackNotebook,
} from "../../utils/reportClient"
import {
  latestNotebookRevision,
  notebookRepeats,
  recipeFromNotebook,
  reportRevisionHistory,
  withKnownScheduleTemplates,
  withoutSchedule,
  withSchedule,
} from "../../utils/reportRecipe"
import { createReportRecipeActions } from "../../utils/reportRecipeActions"
import { OVERVIEW_TARGET } from "../../utils/reportRevisionTypes"
import { effectiveReportScope } from "../../utils/reportScopeCapacity"
import {
  DEFAULT_CONFIG,
  type Bootstrap,
  type Cadence,
  type Notebook,
  type ReportConfig,
} from "../../utils/reportTypes"
import { RenameNotebookDialog } from "../RenameNotebookDialog"
import { Configuration } from "./Configuration"
import { CoverageNotice, emptyCoverage } from "./CoverageNotice"
import { HistoryPane } from "./HistoryPane"
import { LoadingDocument } from "./LoadingDocument"
import { NotebookDocument } from "./NotebookDocument"
import { NotebookReportsTitle } from "./NotebookReportsTitle"
import { ReportConversation } from "./ReportConversation"
import { ReportEmptyState, ReportUnavailableState } from "./ReportEmptyState"
import { ReportMessages } from "./ReportMessages"
import { ReportSchedules } from "./ReportSchedules"
import { ReportSteering } from "./ReportSteering"
import { reportClass } from "./ReportStyles"
import { ReportToolbarActions } from "./ReportToolbarActions"
import { ReportToolbarStatus } from "./ReportToolbarStatus"

export function NotebookReports({
  organizationSlug,
  workspaceId,
  notebookId,
  revision,
  readonly = false,
  environment,
  pageHeader = false,
  onLocationChange,
}: {
  organizationSlug: string
  workspaceId: string
  notebookId?: string
  revision?: string
  readonly?: boolean
  environment?: ReportEnvironment
  /** Names the page in the app's top bar; isolated previews have no top bar. */
  pageHeader?: boolean
  /** Hears each URL the builder writes for itself, before it is written. */
  onLocationChange?: (notebookId?: string, revision?: number) => void
}) {
  const location = useRef({ id: notebookId, revision })
  const {
    request,
    streamRevision,
    notebookUrl,
    updateLocation: writeLocation,
    sharingEnabled,
    scheduleCadences,
    messages,
  } = useReportEnvironment(organizationSlug, workspaceId, environment)
  const updateLocation = (entry?: Notebook, rev?: number) => {
    onLocationChange?.(entry?.id, rev)
    writeLocation(entry, rev)
  }
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null)
  const [notebook, setNotebook] = useState<Notebook | null>(null)
  const [draft, setDraft] = useState<Notebook | null>(null)
  const [config, setConfig] = useState<ReportConfig>(DEFAULT_CONFIG)
  const effectiveConfig = {
    ...config,
    scope: effectiveReportScope(config.scope),
  }
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  const [busy, setBusy] = useState("")
  const [editing, setEditing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [showRecipe, setShowRecipe] = useState(!location.current.id)
  const [showHistory, setShowHistory] = useState(false)
  const [repeat, setRepeat] = useState<Cadence | null>(null)
  const [renaming, setRenaming] = useState(false)
  const { steering, setSteering, steeringRef, selectSection } =
    useReportSteering(notebook?.id)
  const {
    highlight,
    show: showHighlights,
    showTurn,
    clear: clearHighlights,
  } = useReportHighlights()
  const notebookRef = useRef<Notebook | null>(null)
  const [preview, setPreview] = useState(false)
  const latestRevision = useRef(0)
  const activeRequest = useRef<AbortController | null>(null)
  const draftState = useRef({ draft, notebook, dirty: false })
  const dirty = Boolean(
    draft && notebook && JSON.stringify(draft) !== JSON.stringify(notebook),
  )

  draftState.current = { draft, notebook, dirty }
  const installNotebook = useCallback(
    (
      loaded: Notebook,
      isPreview = false,
      hydrateRecipe = false,
      highlightChanges = true,
    ) => {
      const current = notebookRef.current
      if (!highlightChanges && loaded.revision > (current?.revision ?? 0)) {
        clearHighlights()
      }
      if (
        highlightChanges &&
        !isPreview &&
        !hydrateRecipe &&
        current?.id === loaded.id &&
        loaded.revision > current.revision
      ) {
        showHighlights(current, loaded)
      }
      draftState.current = { draft: loaded, notebook: loaded, dirty: false }
      notebookRef.current = loaded
      setNotebook(loaded)
      setDraft(loaded)
      setPreview(isPreview)
      latestRevision.current = latestNotebookRevision(loaded)
      if (hydrateRecipe) {
        setConfig(recipeFromNotebook(loaded))
      }
    },
    [showHighlights, clearHighlights],
  )
  const conversation = useReportConversation(notebook, request, installNotebook)
  const live = useReportLiveRevisions({
    recover: !preview && !readonly && !busy,
    dirty,
    notebook,
    streamRevision,
    request,
    onNotice: setNotice,
    onDocument: (loaded) => {
      const current = notebookRef.current
      if (current?.id === loaded.id && loaded.revision >= current.revision) {
        installNotebook(loaded)
      }
    },
    onRecoveredDocument: (loaded) => {
      const current = notebookRef.current
      if (current?.id === loaded.id && loaded.revision >= current.revision) {
        installNotebook(loaded, false, false, false)
      }
    },
  })
  const locked = Boolean(busy) || live.inFlightCount > 0 || exporting
  const chatTurns = new Map(conversation.turns.map((turn) => [turn.id, turn]))
  live.turns.forEach((turn) => chatTurns.set(turn.id, turn))
  const refreshBootstrap = useCallback(
    async (signal?: AbortSignal) => {
      const loaded = await request<Bootstrap>(
        "/api/bootstrap",
        undefined,
        "GET",
        signal,
      )
      if (!signal?.aborted) {
        setBootstrap((current) => withKnownScheduleTemplates(loaded, current))
      }
      return loaded
    },
    [request],
  )

  useEffect(() => {
    const controller = new AbortController()
    activeRequest.current = controller
    setBusy("Loading workspace")
    void (async () => {
      try {
        await refreshBootstrap(controller.signal)
        const { id, revision } = location.current
        if (id) {
          const loaded = unpackNotebook(
            await request<Notebook | { notebook: Notebook }>(
              `/api/notebooks/${encodeURIComponent(id)}${revision ? `?revision=${encodeURIComponent(revision)}` : ""}`,
              undefined,
              "GET",
              controller.signal,
            ),
          )
          if (!controller.signal.aborted) {
            installNotebook(loaded, Boolean(revision) && !readonly, true)
          }
        }
      } catch (failure: unknown) {
        if (!controller.signal.aborted) {
          setError(reportErrorText(failure))
        }
      } finally {
        if (!controller.signal.aborted) {
          setBusy("")
        }
      }
    })()
    return () => {
      controller.abort()
      activeRequest.current?.abort()
    }
  }, [installNotebook, refreshBootstrap, request, readonly])

  const run = async (
    label: string,
    operation: (signal: AbortSignal) => Promise<unknown>,
    { rethrow = false } = {},
  ) => {
    if (locked) {
      return
    }
    activeRequest.current?.abort()
    const controller = new AbortController()
    activeRequest.current = controller
    setBusy(label)
    setError("")
    setNotice("")
    try {
      await operation(controller.signal)
    } catch (failure: unknown) {
      // A rethrowing caller, such as the rename dialog, shows the failure itself instead of the page banner.
      if (rethrow && !controller.signal.aborted) {
        throw new Error(reportErrorText(failure))
      }
      if (!controller.signal.aborted) {
        setError(reportErrorText(failure))
      }
    } finally {
      if (!controller.signal.aborted) {
        setBusy("")
      }
    }
  }
  // Schedules saved notebooks; a failed attempt retries the schedule only.
  const scheduling = useNotebookSchedule({
    run,
    save: (scheduleReq, signal) =>
      recipes.schedule(
        scheduleReq.template,
        scheduleReq.cadence,
        signal,
        scheduleReq.recipe,
        scheduleReq.existingSchedule,
      ),
    onScheduled: (saved) => {
      setRepeat(null)
      setBootstrap((current) => withSchedule(current, saved.schedule))
      setNotice(saved.message)
    },
  })
  const persistDraft = async (signal: AbortSignal): Promise<Notebook> => {
    const { draft, notebook, dirty } = draftState.current
    if (!draft || !notebook) {
      throw new Error("Open a notebook first.")
    }
    if (!dirty) {
      return notebookRef.current ?? notebook
    }
    const saved = await saveNotebookRevision(
      request,
      draft,
      latestRevision.current,
      signal,
    )
    if (!signal.aborted) {
      installNotebook(saved)
    }
    return saved
  }
  const openNotebook = (id: string, revision?: number) =>
    void run("Loading notebook", async (signal) => {
      if (dirty) {
        await persistDraft(signal)
      }
      const loaded = unpackNotebook(
        await request<Notebook | { notebook: Notebook }>(
          `/api/notebooks/${encodeURIComponent(id)}${revision ? `?revision=${revision}` : ""}`,
          undefined,
          "GET",
          signal,
        ),
      )
      if (signal.aborted) {
        return
      }
      installNotebook(loaded, revision !== undefined, true)
      setEditing(false)
      setShowHistory(false)
      setRepeat(null)
      updateLocation(loaded, revision)
    })

  const generate = () =>
    void run("Generating notebook", async (signal) => {
      if (dirty) {
        await persistDraft(signal)
      }
      const loaded = unpackNotebook(
        await request<Notebook | { notebook: Notebook }>(
          "/api/generate",
          { ...effectiveConfig, request_id: crypto.randomUUID() },
          "POST",
          signal,
        ),
      )
      if (signal.aborted) {
        return
      }
      installNotebook(loaded, false, true)
      setEditing(false)
      setShowRecipe(false)
      setRepeat(null)
      updateLocation(loaded)
      let notice = messages.generated
      if (repeat) {
        try {
          const saved = await recipes.schedule(loaded, repeat, signal)
          setBootstrap((current) => withSchedule(current, saved.schedule))
          notice = `${notice} ${saved.message}`
        } catch (failure: unknown) {
          // The notebook is saved either way; the error says so with the reason.
          notice = ""
          if (!signal.aborted) {
            scheduling.fail({
              template: loaded,
              cadence: repeat,
              recipe: effectiveConfig,
            })
            setError(reportErrorText(failure))
          }
        }
      }
      // Everything above is saved; a failed list refresh must not report it as failed.
      await refreshBootstrap(signal).catch(() => undefined)
      if (notice && !signal.aborted) {
        setNotice(notice)
      }
    })

  const rename = (title: string) =>
    run(
      "Renaming notebook",
      async (signal) => {
        if (!notebook) {
          return
        }
        const saved = await saveNotebookTitle(
          request,
          notebook,
          title,
          latestRevision.current,
          draftState.current.dirty ? undefined : installNotebook,
          signal,
        )
        if (signal.aborted) {
          return
        }
        installNotebook(saved)
        await refreshBootstrap(signal).catch(() => undefined)
        if (!signal.aborted) {
          setNotice(`Renamed. Saved as revision ${saved.revision}.`)
        }
      },
      { rethrow: true },
    )

  const save = () =>
    void run("Saving revision", async (signal) => {
      const saved = await persistDraft(signal)
      await refreshBootstrap(signal)
      if (!signal.aborted) {
        setEditing(false)
        setNotice(`Revision ${saved.revision} saved.`)
      }
    })

  const revise = () => {
    if (!notebook || !steering.trim()) {
      return
    }
    void run("Revising notebook", async (signal) => {
      const loaded = await conversation.revise(steering, signal, persistDraft)
      if (signal.aborted) {
        return
      }
      installNotebook(loaded)
      setEditing(false)
      setSteering("")
      await refreshBootstrap(signal)
      setNotice(
        loaded.chat_history?.at(-1)?.assistant_message ??
          (loaded.revision === notebook.revision
            ? "No notebook changes were made. Your request is in chat history."
            : messages.revised(loaded.revision)),
      )
    })
  }
  const share = () =>
    void run("Preparing share link", async (signal) => {
      const saved = await persistDraft(signal)
      await navigator.clipboard.writeText(notebookUrl(saved, true))
      if (!signal.aborted) {
        setNotice(
          `Copied revision ${saved.revision} link. It is available only to your signed-in account in this workspace.`,
        )
      }
    })

  const recipes = createReportRecipeActions({
    request,
    run,
    config: effectiveConfig,
    notebook,
    persistDraft,
    refreshBootstrap,
    automaticScheduling: bootstrap?.scheduling_available !== false,
    scheduledMessage: (cadence) =>
      bootstrap?.schedule_runtime
        ? `Schedule saved. ${bootstrap.schedule_runtime}`
        : messages.scheduled(cadence),
    onNotice: setNotice,
    onNotebook: (loaded) => {
      installNotebook(loaded, false, true)
      setEditing(false)
      setRepeat(null)
      setShowRecipe(false)
      updateLocation(loaded)
    },
    onRemoved: (id) => setBootstrap((current) => withoutSchedule(current, id)),
    onScheduleUpdated: (updated) =>
      setBootstrap((current) => withSchedule(current, updated)),
  })

  const restore = () =>
    void run("Restoring revision", async (signal) => {
      if (!notebook) {
        return
      }
      const saved = await saveNotebookRevision(
        request,
        notebook,
        latestRevision.current,
        signal,
      )
      if (signal.aborted) {
        return
      }
      installNotebook(saved)
      updateLocation(saved)
      await refreshBootstrap(signal)
      setNotice(`Restored as revision ${saved.revision}.`)
    })

  const newNotebook = () =>
    void run("Preparing notebook", async (signal) => {
      if (dirty) {
        await persistDraft(signal)
      }
      notebookRef.current = null
      setNotebook(null)
      setConfig(DEFAULT_CONFIG)
      setRepeat(null)
      setDraft(null)
      setPreview(false)
      setEditing(false)
      setShowRecipe(true)
      updateLocation()
    })

  return (
    <div
      className={reportClass(`root ${readonly ? "read-only" : ""}`)}
      data-testid="notebook-reports-page"
      data-covi-view="notebook-reports"
    >
      <NotebookReportsTitle
        inTopBar={pageHeader}
        organizationSlug={organizationSlug}
        workspaceId={workspaceId}
        title={notebook?.title ?? null}
        repeats={notebookRepeats(bootstrap, notebook)}
      />
      <RenameNotebookDialog
        open={renaming}
        title={notebook?.title ?? ""}
        onClose={() => setRenaming(false)}
        onRename={rename}
      />
      <div className={reportClass("main-workspace")}>
        <div className={reportClass("workspace-toolbar")}>
          <ReportToolbarStatus
            notebook={notebook}
            readonly={readonly}
            dirty={dirty}
            locked={locked}
            showRecipe={showRecipe}
            onToggleRecipe={() => setShowRecipe(!showRecipe)}
            onNewNotebook={newNotebook}
          />
          {notebook && (
            <ReportToolbarActions
              notebook={notebook}
              organizationSlug={organizationSlug}
              workspaceId={workspaceId}
              locked={locked}
              dirty={dirty}
              editing={editing}
              readonly={readonly}
              preview={preview}
              sharingEnabled={sharingEnabled}
              onExporting={setExporting}
              onHistory={() => setShowHistory(!showHistory)}
              onEdit={() => setEditing(!editing)}
              onRename={() => setRenaming(true)}
              latestHref={readonly ? notebookUrl(notebook, false) : undefined}
              onSave={save}
              onShare={share}
            />
          )}
        </div>
        <ReportMessages
          error={error}
          notice={notice}
          previewRevision={preview ? (notebook?.revision ?? null) : null}
          locked={locked}
          onDismissError={() => setError("")}
          onDismissNotice={() => setNotice("")}
          onLatest={() => notebook && openNotebook(notebook.id)}
          onRestore={restore}
          unscheduled={scheduling.bannerFor(notebook?.id)}
        />
        {!notebook && bootstrap && (
          <CoverageNotice coverage={emptyCoverage(bootstrap, environment)} />
        )}
        <div
          className={reportClass(
            `content-layout ${showRecipe && !readonly ? "with-recipe" : ""}`,
          )}
        >
          {showRecipe && !readonly && bootstrap && (
            <div className={reportClass("recipe-pane")}>
              <Configuration
                key={JSON.stringify([notebook?.id])}
                config={config}
                onChange={setConfig}
                repeat={repeat}
                onRepeatChange={setRepeat}
                repeatCadences={scheduleCadences}
                bootstrap={bootstrap}
                disabled={locked}
                onGenerate={generate}
                onScheduleNotebook={
                  notebook && repeat && !preview
                    ? () =>
                        scheduling.scheduleSaved(notebook, repeat, bootstrap)
                    : undefined
                }
                savedRecipes={
                  <ReportSchedules
                    schedules={bootstrap.schedules}
                    disabled={locked}
                    onRun={recipes.generate}
                    onOpen={openNotebook}
                    onDelete={recipes.remove}
                    onSetEnabled={recipes.setEnabled}
                    automaticScheduling={
                      bootstrap.scheduling_available !== false
                    }
                    metrics={bootstrap.metrics}
                    scopeLimits={bootstrap.scope_limits}
                  />
                }
              />
            </div>
          )}
          <div className={reportClass("document-pane")}>
            {!bootstrap && !busy && error && (
              <ReportUnavailableState
                onRetry={() => void run("Loading workspace", refreshBootstrap)}
              />
            )}
            {busy && (!notebook || busy === "Generating notebook") ? (
              <LoadingDocument action={busy} />
            ) : draft ? (
              <>
                <fieldset
                  className={reportClass("document-fieldset")}
                  disabled={Boolean(busy)}
                >
                  <NotebookDocument
                    notebook={draft}
                    editing={editing && !readonly && !preview}
                    onChange={setDraft}
                    disabled={Boolean(busy)}
                    revisionProgress={live.progress}
                    highlight={
                      highlight.notebookId === draft.id ? highlight : undefined
                    }
                    onReviseSection={
                      !readonly && !preview
                        ? (id) => {
                            const title =
                              id === OVERVIEW_TARGET
                                ? "overview"
                                : draft.sections.find(
                                    (section) => section.id === id,
                                  )?.title
                            selectSection(title)
                          }
                        : undefined
                    }
                  />
                </fieldset>
                <ReportConversation
                  key={draft.id}
                  turns={[...chatTurns.values()].sort((a, b) =>
                    a.created_at.localeCompare(b.created_at),
                  )}
                  busy={locked ? "Updating notebook" : ""}
                  onShowChanges={
                    readonly || preview || !notebook
                      ? undefined
                      : (turn) =>
                          void run("Highlighting changes", async (signal) => {
                            const message = await showTurn(
                              notebook,
                              turn,
                              request,
                              signal,
                            )
                            if (!signal.aborted) {
                              setNotice(message)
                            }
                          })
                  }
                />
                {!readonly && !preview && (
                  <ReportSteering
                    busy={busy}
                    submitDisabled={live.inFlightCount > 0}
                    steering={steering}
                    textareaRef={steeringRef}
                    setSteering={setSteering}
                    revise={revise}
                    activeCount={live.activeCount}
                    queuedCount={live.queuedCount}
                  />
                )}
              </>
            ) : (
              bootstrap && (
                <ReportEmptyState modelAvailable={bootstrap.model_available} />
              )
            )}
          </div>
          {showHistory && notebook && (
            <HistoryPane
              notebook={notebook}
              revisions={reportRevisionHistory(notebook)}
              busy={locked ? "Updating notebook" : ""}
              onClose={() => setShowHistory(false)}
              openNotebook={openNotebook}
            />
          )}
        </div>
      </div>
    </div>
  )
}
